import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Box, Tag, Users, Search, ArrowLeft, Package, Truck, Layers } from "lucide-react";
import authAxios from "../../api/authAxios";
import { NotificationContext } from "../../context/NotificationContext";

export default function OwnerInventoryReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ products: [], categories: [], suppliers: [], stock_inwards: [] });
  const [owner, setOwner] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("products");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [res, userRes] = await Promise.all([
        authAxios.get(`/api/super-admin/owners/${id}/owner_details/`),
        authAxios.get(`/api/super-admin/owners/${id}/`)
      ]);
      setData(res.data);
      setOwner(userRes.data);
    } catch (error) {
      addNotification("Error loading inventory data", "error");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (activeSubTab === "products") return data.products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.product_code.toLowerCase().includes(searchTerm.toLowerCase()));
    if (activeSubTab === "suppliers") return data.suppliers.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return [];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-bold text-sm uppercase tracking-wider">Back to User Management</span>
        </button>

        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl border border-slate-200 dark:border-slate-700 mb-8 relative overflow-hidden">
            <div className="absolute top-[-20px] right-[-20px] opacity-5">
                <Box className="w-64 h-64" />
            </div>
            
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-start lg:items-center gap-6">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Master Inventory Report</h1>
                    {owner && (
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full text-xs font-black uppercase tracking-widest border border-emerald-500/20">
                            {owner.last_name || owner.business_name}
                        </span>
                    )}
                </div>
                
                <div className="flex gap-4">
                    <div className="bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl flex">
                        {[
                            { id: "products", label: "Products", icon: Package },
                            { id: "suppliers", label: "Suppliers", icon: Truck },
                            { id: "categories", label: "Categories", icon: Layers },
                            { id: "stock", label: "Stock Logs", icon: Box }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSubTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                    activeSubTab === tab.id ? "bg-white dark:bg-slate-800 text-blue-600 shadow-md" : "text-slat-500 opacity-60"
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 bg-slate-50/50 dark:bg-slate-900/20 border-b border-slate-100 dark:border-slate-700">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                        type="text"
                        placeholder="Search inventory records..."
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="p-6">
                {activeSubTab === "products" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.products.map(prod => (
                            <div key={prod.id} className="p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 rounded-2xl flex justify-between items-center">
                                <div>
                                    <p className="font-black text-slate-900 dark:text-white">{prod.name}</p>
                                    <p className="text-xs text-slate-400 font-mono">{prod.product_code}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-lg font-black text-blue-600">₹{prod.unit_price}</p>
                                    <p className="text-[10px] font-black uppercase text-slate-400">Stock: {prod.stock}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeSubTab === "suppliers" && (
                    <div className="space-y-3">
                        {data.suppliers.map(sup => (
                            <div key={sup.id} className="p-4 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 rounded-2xl flex justify-between items-center group hover:border-blue-500/50 transition-all">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-600 font-black">
                                        {sup.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-900 dark:text-white">{sup.name}</p>
                                        <p className="text-xs text-slate-500">{sup.phone || "No phone"}</p>
                                    </div>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${sup.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100'}`}>
                                    {sup.status}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {activeSubTab === "categories" && (
                    <div className="flex flex-wrap gap-3">
                        {data.categories.map(cat => (
                            <div key={cat.id} className="px-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl font-black text-sm text-slate-700 dark:text-slate-300 shadow-sm">
                                {cat.name}
                            </div>
                        ))}
                    </div>
                )}

                {activeSubTab === "stock" && (
                     <div className="space-y-2">
                        {data.stock_inwards.map(stock => (
                            <div key={stock.id} className="flex justify-between items-center text-sm p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                                <div className="flex flex-col">
                                    <span className="font-bold text-slate-900 dark:text-white">{stock.product_name}</span>
                                    <span className="text-xs text-slate-400">Batch: {stock.batch_number || 'N/A'}</span>
                                </div>
                                <div className="flex flex-col items-end">
                                    <span className="font-black text-emerald-600">+{stock.received_quantity} qty</span>
                                    <span className="text-[10px] font-bold text-slate-500 uppercase">{new Date(stock.received_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
}
